import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Pencil } from 'lucide-react';
import { LeadCardEditor, type LeadCardDraft } from '../../components/competition/LeadCardEditor';
import { LeadCardSummary } from '../../components/competition/LeadCardSummary';
import {
  permittedLeadStyles,
  type LeadClassStyles,
} from '../../components/competition/lead-climb-style';
import { errorCodeOf } from '../../services/api-error';
import { roundScoresService, type RoundScore, type RoundScoreClimb } from '../../services/round-scores';

interface LiveLeadCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competitionSlug: string;
  roundId: string;
  score: RoundScore | null;
  /** The round's routes, for the holds each card is read against. */
  climbs: RoundScoreClimb[];
  /**
   * How each class climbs the round, for what a card's style switch offers
   * (`permittedLeadStyles`, together with the route's own forcing).
   */
  classStyles?: LeadClassStyles;
  /** Whether the round still takes cards. False once it is closed. */
  canScore: boolean;
  /** The card as it stands when the dialog closes, for the row behind it. */
  onClosed: (score: RoundScore) => void;
  /** The server having said the round is closed, so the page can agree. */
  onRoundClosed?: () => void;
}

/**
 * One person's lead round, one row per route: how far up and how long on each,
 * edited a row at a time.
 *
 * The counterpart of `LiveScoreCardDialog` for a lead round, and the difference is
 * the one the discipline makes: a boulder card is a stream of presses that are each
 * saved as they happen, a lead card is two numbers typed after the climber comes
 * off the wall. So each row is a draft with its own save — and like the boulder
 * dialog, the page behind is told the final card when this closes rather than
 * redrawn under it on every write.
 */
export default function LiveLeadCardDialog({
  open,
  onOpenChange,
  competitionSlug,
  roundId,
  score,
  climbs,
  classStyles,
  canScore,
  onClosed,
  onRoundClosed,
}: LiveLeadCardDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [card, setCard] = useState<RoundScore | null>(null);
  /** The one route whose card is open, and the one with a save in flight. */
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (open && score) setCard(score);
    if (!open) setEditing(null);
  }, [open, score]);

  const write = async (roundClimbId: string, draft: LeadCardDraft) => {
    if (!card || busy || !canScore) return;

    setBusy(roundClimbId);
    try {
      const saved = await roundScoresService.save(competitionSlug, roundId, card.id, [
        {
          round_climb_id: roundClimbId,
          holds_count: draft.holds,
          time_seconds: draft.timeSeconds,
          climbed_as: draft.style ?? null,
        },
      ]);
      setCard(saved);
      setEditing(null);
    } catch (error) {
      toast({
        title: t('gymAdmin.roundScores.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      if (errorCodeOf(error) === 'round_closed') onRoundClosed?.();
    } finally {
      setBusy(null);
    }
  };

  const close = (next: boolean) => {
    if (next) return;
    if (card) onClosed(card);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[85svh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{card?.full_name}</DialogTitle>
          <DialogDescription>
            {canScore
              ? t('leadRounds.dialogDescription')
              : t('gymAdmin.roundScores.closedDialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {(card?.cards ?? []).map((row) => {
            const route = climbs.find((climb) => climb.id === row.round_climb_id);
            const writable = canScore && row.can_score !== false;

            return (
              <div
                key={row.round_climb_id}
                className="space-y-2 rounded-lg border p-3"
                data-testid="live-lead-row"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">
                    {row.climb_name ?? t('gymAdmin.roundScores.unnamedClimb')}
                    {route?.holds_count != null && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {t('leadRounds.holdsOnRoute', { count: route.holds_count })}
                      </span>
                    )}
                  </span>

                  <div className="flex items-center gap-2">
                    <LeadCardSummary
                      holds={row.holds_count}
                      timeSeconds={row.time_seconds}
                      formattedTime={row.formatted_time}
                      topped={row.topped}
                      climbedAs={row.climbed_as}
                    />
                    {writable && editing !== row.round_climb_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1.5 text-xs"
                        onClick={() => setEditing(row.round_climb_id)}
                        data-testid="open-lead-row"
                      >
                        <Pencil className="size-3" aria-hidden />
                        {row.holds_count == null
                          ? t('leadRounds.recordCard')
                          : t('leadRounds.editCard')}
                      </Button>
                    )}
                  </div>
                </div>

                {editing === row.round_climb_id && (
                  <LeadCardEditor
                    holds={row.holds_count ?? null}
                    timeSeconds={row.time_seconds ?? null}
                    climbedAs={row.climbed_as}
                    styles={permittedLeadStyles(route, card?.category_id, classStyles)}
                    maxHolds={route?.holds_count}
                    saving={busy === row.round_climb_id}
                    disabled={!writable}
                    onSave={(draft) => write(row.round_climb_id, draft)}
                    onClear={() =>
                      write(row.round_climb_id, { holds: '', timeSeconds: null, style: null })
                    }
                    data-testid="live-lead-editor"
                  />
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
